import { useEffect, useRef } from 'react';
import { createConsumer } from '@rails/actioncable';
import {
  parseConversationUpdatedCable,
  parseMessageCreatedCable,
  parseTypingCableEvent,
  shouldRefetchFromCable,
} from '../utils/boardChatUtils';

/**
 * ActionCable do Chatwoot (RoomChannel) — navegador → Chatwoot.
 * @param {object} session
 * @param {() => void} onEvent — refetch lista/thread
 * @param {boolean} enabled
 * @param {(typing: object) => void} [onTyping]
 * @param {(patch: object) => void} [onConversationPatch] — patch local (unread_count etc.)
 * @param {(msg: object) => void} [onMessageCreated] — badge / realtime leve
 */
export function useBoardChatCable(
  session,
  onEvent,
  enabled = true,
  onTyping,
  onConversationPatch,
  onMessageCreated
) {
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const onTypingRef = useRef(onTyping);
  onTypingRef.current = onTyping;
  const onPatchRef = useRef(onConversationPatch);
  onPatchRef.current = onConversationPatch;
  const onMessageCreatedRef = useRef(onMessageCreated);
  onMessageCreatedRef.current = onMessageCreated;
  const timerRef = useRef(null);

  useEffect(() => {
    if (!enabled || !session?.pubsubToken || !session?.wsBaseUrl) return undefined;
    if (!session.chatwootAccountId || !session.chatwootUserId) return undefined;

    const base = String(session.wsBaseUrl).replace(/\/$/, '');
    const cableUrl = `${base}/cable`;

    const scheduleRefetch = () => {
      if (!onEventRef.current) return;
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        onEventRef.current?.();
      }, 400);
    };

    let consumer;
    let subscription;
    try {
      consumer = createConsumer(cableUrl);
      subscription = consumer.subscriptions.create(
        {
          channel: 'RoomChannel',
          pubsub_token: session.pubsubToken,
          account_id: Number(session.chatwootAccountId),
          user_id: Number(session.chatwootUserId),
        },
        {
          received(data) {
            const typing = parseTypingCableEvent(data);
            if (typing) {
              onTypingRef.current?.(typing);
              return;
            }
            const patch = parseConversationUpdatedCable(data);
            if (patch) {
              onPatchRef.current?.(patch);
            }
            const created = parseMessageCreatedCable(data);
            if (created) {
              onMessageCreatedRef.current?.(created);
            }
            if (shouldRefetchFromCable(data)) scheduleRefetch();
          },
        }
      );
    } catch {
      return undefined;
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      try {
        subscription?.unsubscribe();
      } catch {
        /* ignore */
      }
      try {
        consumer?.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [
    enabled,
    session?.pubsubToken,
    session?.wsBaseUrl,
    session?.chatwootAccountId,
    session?.chatwootUserId,
  ]);
}
